import React, { useState, useEffect, useMemo } from "react";
import {
  FaPlus,
  FaChevronDown,
  FaChevronUp,
  FaHistory,
  FaDonate,
  FaMinus,
  FaPowerOff,
  FaHandHoldingUsd,
  FaChartLine,
} from "react-icons/fa";
import Button from "./ui/button";
import Input from "./ui/input";
import confetti from "canvas-confetti";

const colors = [
  "#F44336", "#E91E63", "#9C27B0", "#673AB7", "#3F51B5",
  "#2196F3", "#03A9F4", "#00BCD4", "#009688", "#4CAF50",
  "#8BC34A", "#CDDC39", "#dece38", "#FFC107", "#FF9800",
  "#FF5722", "#795548", "#607D8B", "#8E24AA", "#3949AB",
  "#1E88E5", "#039BE5", "#00897B", "#43A047", "#7CB342",
  "#C0CA33", "#FDD835", "#FB8C00", "#F4511E", "#6D4C41",
  "#546E7A", "#D81B60", "#5E35B1", "#00ACC1", "#7E57C2",
];

const SCORE_CHART_STORAGE_PREFIX = "scoreChartHistory_";
const MAX_VISIBLE_CHART_POINTS = 200;

const getRandomColor = (usedColors) => {
  const available = colors.filter(
    (color) => !usedColors.includes(color)
  );

  return available.length
    ? available[Math.floor(Math.random() * available.length)]
    : "#7E57C2";
};

const getSnapshotPlayer = (snapshot, playerId) => {
  if (
    snapshot?.players &&
    Object.prototype.hasOwnProperty.call(
      snapshot.players,
      playerId
    )
  ) {
    return snapshot.players[playerId];
  }

  // Tương thích dữ liệu chart cũ
  if (
    snapshot?.scores &&
    Object.prototype.hasOwnProperty.call(
      snapshot.scores,
      playerId
    )
  ) {
    return {
      score: Number(snapshot.scores[playerId]) || 0,
      active: true,
    };
  }

  return null;
};

const ScoreTrendChart = ({ players, history }) => {
  if (!players?.length) {
    return (
      <div className="score-chart-empty">
        Chưa có người chơi
      </div>
    );
  }

  if (!history?.length) {
    return (
      <div className="score-chart-empty">
        Chưa có biến động điểm
      </div>
    );
  }

  const displayHistory =
    history.length > MAX_VISIBLE_CHART_POINTS
      ? history.slice(-MAX_VISIBLE_CHART_POINTS)
      : history;

  const allScores = [];

  displayHistory.forEach((snapshot) => {
    Object.values(
      snapshot.players || snapshot.scores || {}
    ).forEach((item) => {
      const score =
        typeof item === "object"
          ? Number(item.score) || 0
          : Number(item) || 0;

      allScores.push(score);
    });
  });

  allScores.push(0);

  let minScore = Math.min(...allScores);
  let maxScore = Math.max(...allScores);

  if (minScore === maxScore) {
    minScore = -100;
    maxScore = 100;
  }

  const range = maxScore - minScore;
  const padding = Math.max(20, range * 0.12);

  minScore -= padding;
  maxScore += padding;

  const rawStep = (maxScore - minScore) / 4;
  const magnitude =
    Math.pow(10, Math.floor(Math.log10(rawStep)));

  const normalized = rawStep / magnitude;

  const niceNormalized =
    normalized <= 1
      ? 1
      : normalized <= 2
      ? 2
      : normalized <= 5
      ? 5
      : 10;

  const niceStep =
    niceNormalized * magnitude;

  const chartMin =
    Math.floor(minScore / niceStep) * niceStep;

  const chartMax =
    Math.ceil(maxScore / niceStep) * niceStep;

  const WIDTH = 1000;
  const HEIGHT = 390;

  const PADDING = {
    top: 22,
    right: 185,
    bottom: 45,
    left: 58,
  };

  const chartWidth =
    WIDTH -
    PADDING.left -
    PADDING.right;

  const chartHeight =
    HEIGHT -
    PADDING.top -
    PADDING.bottom;

  const x = (index) => {
    if (displayHistory.length <= 1) {
      return PADDING.left;
    }

    return (
      PADDING.left +
      (index /
        (displayHistory.length - 1)) *
        chartWidth
    );
  };

  const y = (score) => {
    const value = Number.isFinite(
      Number(score)
    )
      ? Number(score)
      : 0;

    return (
      PADDING.top +
      ((chartMax - value) /
        (chartMax - chartMin)) *
        chartHeight
    );
  };

  const formatScore = (score) => {
    const value = Number(score) || 0;
    return value > 0
      ? `+${value}`
      : `${value}`;
  };

  const gridValues = [];

  for (
    let value = chartMin;
    value <= chartMax + niceStep / 10;
    value += niceStep
  ) {
    gridValues.push(value);
  }

  const getSegments = (player) => {
    const segments = [];
    let current = [];

    displayHistory.forEach(
      (snapshot, index) => {
        const item =
          getSnapshotPlayer(
            snapshot,
            player.id
          );

        // Chưa tham gia hoặc đã OFF => kết thúc line
        if (
          !item ||
          item.active === false
        ) {
          if (current.length) {
            segments.push(current);
            current = [];
          }

          return;
        }

        current.push({
          x: x(index),
          y: y(item.score),
        });
      }
    );

    if (current.length) {
      segments.push(current);
    }

    return segments;
  };

  const getLastPoint = (player) => {
    for (
      let i = displayHistory.length - 1;
      i >= 0;
      i--
    ) {
      const item =
        getSnapshotPlayer(
          displayHistory[i],
          player.id
        );

      if (item) {
        return {
          index: i,
          score:
            Number(item.score) || 0,
          active:
            item.active !== false,
        };
      }
    }

    return null;
  };

  const zeroVisible =
    chartMin <= 0 &&
    chartMax >= 0;

  const zeroY = zeroVisible
    ? y(0)
    : null;

  const xLabelIndexes = [];
  const labelCount = Math.min(
    6,
    displayHistory.length
  );

  if (displayHistory.length === 1) {
    xLabelIndexes.push(0);
  } else {
    for (
      let i = 0;
      i < labelCount;
      i++
    ) {
      const index = Math.round(
        (i /
          (labelCount - 1)) *
          (displayHistory.length - 1)
      );

      if (
        !xLabelIndexes.includes(index)
      ) {
        xLabelIndexes.push(index);
      }
    }
  }

  return (
    <div className="score-chart">

      <div className="score-chart-meta">
       
        {history.length >
          MAX_VISIBLE_CHART_POINTS && (
          <span>
            {" "}
             đang xem{" "}
            {MAX_VISIBLE_CHART_POINTS} lượt gần nhất
          </span>
        )}
      </div>

      <div className="score-chart-scroll">

        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="score-chart-svg"
          role="img"
          aria-label="Biểu đồ diễn biến điểm"
        >

          {gridValues.map((value) => {
            const gy = y(value);

            return (
              <g key={`grid-${value}`}>

                <line
                  x1={PADDING.left}
                  x2={
                    PADDING.left +
                    chartWidth
                  }
                  y1={gy}
                  y2={gy}
                  className={
                    value === 0
                      ? "score-chart-zero-line"
                      : "score-chart-grid-line"
                  }
                />

                <text
                  x={PADDING.left - 10}
                  y={gy + 4}
                  textAnchor="end"
                  className="score-chart-axis-text"
                >
                  {value > 0
                    ? `+${Math.round(value)}`
                    : Math.round(value)}
                </text>

              </g>
            );
          })}

          <line
            x1={PADDING.left}
            x2={PADDING.left}
            y1={PADDING.top}
            y2={
              PADDING.top +
              chartHeight
            }
            className="score-chart-axis-line"
          />

          {zeroVisible && (
            <circle
              cx={PADDING.left}
              cy={zeroY}
              r="6"
              className="score-chart-zero-dot"
            />
          )}

          {players.map((player) => {
            const segments =
              getSegments(player);

            const lastPoint =
              getLastPoint(player);

            if (!lastPoint) {
              return null;
            }

            const endX =
              x(lastPoint.index);

            const endY =
              y(lastPoint.score);

            const labelY = Math.max(
              20,
              Math.min(
                HEIGHT - 20,
                endY
              )
            );

            return (
              <g
                key={`player-${player.id}`}
              >

                {segments.map(
                  (
                    segment,
                    segmentIndex
                  ) => {
                    const path =
                      segment
                        .map(
                          (
                            point,
                            index
                          ) =>
                            `${
                              index ===
                              0
                                ? "M"
                                : "L"
                            } ${
                              point.x
                            } ${
                              point.y
                            }`
                        )
                        .join(" ");

                    return (
                      <path
                        key={`line-${player.id}-${segmentIndex}`}
                        d={path}
                        fill="none"
                        stroke={
                          player.color
                        }
                        strokeWidth="3.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={
                          lastPoint.active
                            ? "score-trend-line"
                            : "score-trend-line score-trend-line-inactive"
                        }
                      />
                    );
                  }
                )}

                <circle
                  cx={endX}
                  cy={endY}
                  r="5"
                  fill={player.color}
                  className="score-trend-end-dot"
                  opacity={
                    lastPoint.active
                      ? 1
                      : 0.5
                  }
                />

                <line
                  x1={endX + 4}
                  x2={endX + 13}
                  y1={endY}
                  y2={labelY}
                  stroke={player.color}
                  strokeWidth="2"
                  opacity={
                    lastPoint.active
                      ? 0.8
                      : 0.4
                  }
                />

                <g
                  transform={`translate(${
                    endX + 15
                  }, ${labelY})`}
                  opacity={
                    lastPoint.active
                      ? 1
                      : 0.55
                  }
                >

                  <rect
                    x="0"
                    y="-12"
                    width="150"
                    height="25"
                    rx="10"
                    fill={player.color}
                    className="score-player-label"
                    opacity={
                      lastPoint.active
                        ? 1
                        : 0.55
                    }
                  />

                  <circle
                    cx="12"
                    cy="0"
                    r="4"
                    fill="rgba(255,255,255,.85)"
                  />

                  <text
                    x="23"
                    y="5"
                    className="score-player-label-text"
                    opacity={
                      lastPoint.active
                        ? 1
                        : 0.55
                    }
                  >
                    {player.name}{" "}
                    {formatScore(
                      lastPoint.score
                    )}
                    {!lastPoint.active
                      ? " · OFF"
                      : ""}
                      
                  </text>

                </g>
              </g>
            );
          })}

          <line
            x1={PADDING.left}
            x2={
              PADDING.left +
              chartWidth
            }
            y1={
              PADDING.top +
              chartHeight
            }
            y2={
              PADDING.top +
              chartHeight
            }
            className="score-chart-axis-line"
          />

          {xLabelIndexes.map(
            (index) => (
              <text
                key={`xlabel-${index}`}
                x={x(index)}
                y={
                  PADDING.top +
                  chartHeight +
                  23
                }
                textAnchor="middle"
                className="score-chart-axis-text"
              >
                {displayHistory[
                  index
                ]?.sequence ??
                  index + 1}
              </text>
            )
          )}

          <text
            x={
              PADDING.left +
              chartWidth / 2
            }
            y={HEIGHT - 7}
            textAnchor="middle"
            className="score-chart-x-title"
          >
            Lượt
          </text>

        </svg>
      </div>
    </div>
  );
};

const PlayerList = ({
  game,
  setGame,
}) => {
  const [playerName, setPlayerName] =
    useState("");

  const [history, setHistory] =
    useState(null);

  const [collapsed, setCollapsed] =
    useState(false);

  const [
    showConfirmModal,
    setShowConfirmModal,
  ] = useState(false);

  const [
    playerToDeactivate,
    setPlayerToDeactivate,
  ] = useState(null);

  const [
    scoreChanging,
    setScoreChanging,
  ] = useState(null);

  const [
    scoreChangeAmount,
    setScoreChangeAmount,
  ] = useState(null);

  const [
    showMessageSetHost,
    setShowMessageSetHost,
  ] = useState(false);

  const [
    inputPoints,
    setInputPoints,
  ] = useState({});

  const [
    chartCollapsed,
    setChartCollapsed,
  ] = useState(true);

  const [
    scoreChartHistory,
    setScoreChartHistory,
  ] = useState([]);

  const [
    winnerDeductions,
    setWinnerDeductions,
  ] = useState([]);

  const chartStorageKey = useMemo(() => {
    const gameKey =
      game?.id ??
      game?.name ??
      "default";

    return `${SCORE_CHART_STORAGE_PREFIX}${gameKey}`;
  }, [
    game?.id,
    game?.name,
  ]);

  useEffect(() => {
    const storedGame =
      localStorage.getItem(
        "score3cnoteData"
      );

    if (storedGame) {
      try {
        setGame(
          JSON.parse(storedGame)
        );
      } catch (error) {
        console.error(
          "Không thể đọc score3cnoteData:",
          error
        );
      }
    } else {
      setGame({
        id: null,
        players: [],
        name:
          "game_" +
          Date.now(),
        coefficient: 5,
        timestamp: Date.now(),
      });
    }
  }, [setGame]);

  useEffect(() => {
    const storedHistory =
      localStorage.getItem(
        chartStorageKey
      );

    if (!storedHistory) {
      setScoreChartHistory([]);
      return;
    }

    try {
      const parsed =
        JSON.parse(
          storedHistory
        );

      setScoreChartHistory(
        Array.isArray(parsed)
          ? parsed
          : []
      );
    } catch (error) {
      console.error(
        "Không thể đọc score chart history:",
        error
      );

      setScoreChartHistory([]);
    }
  }, [chartStorageKey]);

  useEffect(() => {
    if (game?.players?.length) {
      localStorage.setItem(
        "score3cnoteData",
        JSON.stringify(game)
      );
    }
  }, [game]);

  const saveScoreChartSnapshot = (
    players,
    eventType = "score"
  ) => {
    const nextSequence =
      scoreChartHistory.length > 0
        ? (
            Number(
              scoreChartHistory[
                scoreChartHistory.length - 1
              ].sequence
            ) ||
            scoreChartHistory.length
          ) + 1
        : 1;

    const snapshot = {
      sequence:
        nextSequence,

      timestamp:
        Date.now(),

      eventType,

      players:
        players.reduce(
          (result, player) => {
            result[player.id] = {
              score:
                Number(
                  player.score
                ) || 0,

              active:
                player.isActive !== false,
            };

            return result;
          },
          {}
        ),
    };

    const newHistory = [
      ...scoreChartHistory,
      snapshot,
    ];

    localStorage.setItem(
      chartStorageKey,
      JSON.stringify(
        newHistory
      )
    );

    setScoreChartHistory(
      newHistory
    );
  };

  const handleAddPlayer = () => {
    const name =
      playerName.trim();

    if (!name) return;

    const color =
      getRandomColor(
        game.players.map(
          (p) => p.color
        )
      );

    const newPlayer = {
      id: Date.now(),
      name,
      color,
      score: 0,
      isHost: false,
      isActive: true,
    };

    const updatedPlayers = [
      ...(game.players || []),
      newPlayer,
    ];

    setGame(
      (prevGame) => ({
        ...prevGame,
        players:
          updatedPlayers,
      })
    );

    // ID mới => player mới, kể cả trùng tên
    saveScoreChartSnapshot(
      updatedPlayers,
      "join"
    );

    setPlayerName("");
  };

  const handleInputChange = (
    playerId,
    value
  ) => {
    setInputPoints(
      (prev) => ({
        ...prev,
        [playerId]:
          value,
      })
    );
  };

  const handleUpdateScore = (
    playerId,
    operation
  ) => {
    const inputPoint =
      parseInt(
        inputPoints[
          playerId
        ],
        10
      );

    if (isNaN(inputPoint)) {
      alert(
        "Vui lòng nhập một giá trị hợp lệ!"
      );
      return;
    }

    if (inputPoint <= 0) {
      alert(
        "Số điểm phải lớn hơn 0!"
      );
      return;
    }
    const activePlayers =
      game.players.filter(
        (player) => player.isActive
    );
    const maxDeductions =
    activePlayers.length - 1;

    const pointChange =
      operation === "add"
        ? inputPoint
        : -inputPoint;

    if (operation === "add") {
      setWinnerDeductions((prev) => [
        ...prev,
        -inputPoint,
      ].slice(-maxDeductions));
    }
    if (operation === "subtract") {
      setWinnerDeductions((prev) => [
        ...prev,
        inputPoint,
      ].slice(-maxDeductions));
    }

    const updatedPlayers =
      game.players.map(
        (player) =>
          player.id === playerId
            ? {
                ...player,
                score:
                  player.score +
                  pointChange,
              }
            : player
      );
    // Balance để tổng điểm = 0
    const totalScore =
      updatedPlayers.reduce(
        (total, player) =>
          total +
          (Number(
            player.score
          ) || 0),
        0
      );

    const hostScoreChange =
      -totalScore;

    const updatedPlayersWithHost =
      updatedPlayers.map(
        (player) =>
          player.isHost
            ? {
                ...player,
                score:
                  player.score +
                  hostScoreChange,
              }
            : player
      );


      const winner = updatedPlayersWithHost.find(
        (player) => player.isHost
      );

    const historyEntry = {
      playerId,
      change: pointChange,
      winnerName: winner?.name || "",
      timestamp:
        Date.now(),
    };

    const playerHistory =
      JSON.parse(
        localStorage.getItem(
          `playerScoreHistory_${playerId}`
        ) || "[]"
      );

    playerHistory.push(
      historyEntry
    );

    if (
      playerHistory.length >
      35
    ) {
      playerHistory.shift();
    }

    localStorage.setItem(
      `playerScoreHistory_${playerId}`,
      JSON.stringify(
        playerHistory
      )
    );

    // Lưu sau balance
    saveScoreChartSnapshot(
      updatedPlayersWithHost,
      "score"
    );

    setGame(
      (prevGame) => ({
        ...prevGame,
        players:
          updatedPlayersWithHost,
      })
    );

    setInputPoints(
      (prev) => ({
        ...prev,
        [playerId]: "",
      })
    );

    setScoreChanging(
      playerId
    );

    setScoreChangeAmount(
      pointChange
    );

    setTimeout(() => {
      setScoreChanging(null);
      setScoreChangeAmount(null);
    }, 3000);
  };

  const handleViewHistory = (
    playerId
  ) => {
    const playerHistory =
      JSON.parse(
        localStorage.getItem(
          `playerScoreHistory_${playerId}`
        ) || "[]"
      );

    setHistory(
      playerHistory.filter(
        (entry) =>
          entry.playerId ===
          playerId
      )
    );
  };

  const handleSetHost = (
    playerId
  ) => {
    setWinnerDeductions([]);

    const updatedPlayers =
      game.players.map(
        (player) =>
          player.id === playerId
            ? {
                ...player,
                isHost: true,
              }
            : {
                ...player,
                isHost: false,
              }
      );

    setGame(
      (prevGame) => ({
        ...prevGame,
        players:
          updatedPlayers,
      })
    );

    confetti({
      particleCount: 150,
      spread: 70,
      origin: {
        y: 0.6,
      },
    });

    setShowMessageSetHost(
      true
    );

    setTimeout(
      () =>
        setShowMessageSetHost(
          false
        ),
      4000
    );
  };

  const handleDeactivatePlayer = (
    playerId
  ) => {
    setPlayerToDeactivate(
      playerId
    );

    setShowConfirmModal(
      true
    );
  };

  const confirmDeactivatePlayer =
    () => {
      if (
        playerToDeactivate ==
        null
      ) {
        return;
      }

      const updatedPlayers =
        game.players.map(
          (player) =>
            player.id ===
            playerToDeactivate
              ? {
                  ...player,
                  isActive:
                    false,
                }
              : player
        );

      setGame(
        (prevGame) => ({
          ...prevGame,
          players:
            updatedPlayers,
        })
      );

      // Snapshot OFF: từ đây line của player sẽ dừng
      saveScoreChartSnapshot(
        updatedPlayers,
        "deactivate"
      );

      setShowConfirmModal(
        false
      );

      setPlayerToDeactivate(
        null
      );
    };

  const cancelDeactivatePlayer =
    () => {
      setShowConfirmModal(
        false
      );

      setPlayerToDeactivate(
        null
      );
    };

  const sortedPlayers = [
    ...(game?.players || []),
  ].sort((a, b) => {
    if (
      a.isActive &&
      !b.isActive
    ) {
      return -1;
    }

    if (
      !a.isActive &&
      b.isActive
    ) {
      return 1;
    }

    if (
      a.isHost &&
      !b.isHost
    ) {
      return -1;
    }

    if (
      !a.isHost &&
      b.isHost
    ) {
      return 1;
    }

    return 0;
  });

  // Giữ inactive để chart vẫn có lịch sử
  const chartPlayers =
    game?.players || [];

  const winner =
    game?.players?.find(
      (player) =>
        player.isHost
    );

  return (
    <div className="player-list-container">

      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() =>
          setCollapsed(
            (prev) => !prev
          )
        }
      >
        <span className="font-bold txt-add-play">
          Thêm Người
        </span>

        {collapsed ? (
          <FaChevronDown size={18} />
        ) : (
          <FaChevronUp size={18} />
        )}
      </div>

      {!collapsed && (
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">

          <Input
            value={playerName}
            onChange={(e) =>
              setPlayerName(
                e.target.value
              )
            }
            placeholder="Nhập tên người chơi"
            className="flex-1 p-3 border rounded-md text-lg input-field"
          />

          <Button
            onClick={
              handleAddPlayer
            }
            className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 text-lg rounded-lg shadow-lg add-player-button"
          >
            Thêm Người Chơi
          </Button>

        </div>
      )}

      {sortedPlayers.map(
        (player) => (
          <div
            key={player.id}
            className={`player-item ${
              !player.isActive
                ? "player-inactive"
                : ""
            }`}
            style={{
              backgroundColor:
                player.color,
            }}
          >

            <div className="player-info">

              <span className="player-name">
                {player.isHost ? (
                  <FaHandHoldingUsd
                    size={20}
                  />
                ) : (
                  ""
                )}

                {player.name}
              </span>

              <span
                className={`player-role ${
                  player.isHost
                    ? "shining-text"
                    : ""
                }`}
              >
                {player.isHost
                  ? " 🌟Winner🌟"
                  : " 🎮"}
              </span>

              <span className="player-score">
                {player.score}

                {player.isHost &&
                  winnerDeductions.map(
                    (point, index) => (
                      <span
                        key={`${player.id}-${index}`}
                        className="winner-deduction"
                      >
                        {point > 0 ? `+${point}` : point}
                      </span>
                    )
                  )}
              </span>

              {scoreChanging ===
                player.id &&
                scoreChangeAmount && (
                  <span className="score-change-animation">
                    {scoreChangeAmount >
                    0
                      ? `+${scoreChangeAmount}`
                      : scoreChangeAmount}
                  </span>
                )}

            </div>

            <div className="player-actions">

              {!player.isHost &&
                player.isActive && (
                  <div className="flex space-x-2">
<Button onClick={() => handleUpdateScore(player.id, "add")} className="update-score-button btn-cus mg-l-6">
                  <FaPlus size={20} />
                </Button>
                    <Input
                      type="number"
                      value={
                        inputPoints[
                          player.id
                        ] || ""
                      }
                      onChange={(e) =>
                        handleInputChange(
                          player.id,
                          e.target.value
                        )
                      }
                      placeholder="0"
                      min="0"
                      inputMode="numeric"
                      className="flex-1 p-3 border rounded-md text-lg input-field txtNumPoint"
                    />

                    <Button
                      onClick={() =>
                        handleUpdateScore(
                          player.id,
                          "subtract"
                        )
                      }
                      className="update-score-button btn-cus mg-l-6"
                    >
                      <FaMinus
                        size={20}
                      />
                    </Button>

                  </div>
                )}

              {!player.isHost &&
                player.isActive && (
                  <Button
                    onClick={() =>
                      handleSetHost(
                        player.id
                      )
                    }
                    className="set-host-button btn-host-cus"
                  >
                    <FaDonate
                      size={20}
                    />
                  </Button>
                )}

              {!player.isHost &&
                player.isActive && (
                  <Button
                    onClick={() =>
                      handleViewHistory(
                        player.id
                      )
                    }
                    className="view-history-button"
                  >
                    <FaHistory
                      size={20}
                    />
                  </Button>
                )}

              {!player.isHost &&
                player.isActive && (
                  <Button
                    onClick={() =>
                      handleDeactivatePlayer(
                        player.id
                      )
                    }
                    className="deactivate-player-button flex items-center space-x-2"
                  >
                    <FaPowerOff
                      size={20}
                    />
                  </Button>
                )}

            </div>
          </div>
        )
      )}

      {/* Chart bottom */}
      <div
        className={`balance-panel ${
          chartCollapsed
            ? "collapsed"
            : ""
        }`}
      >

        <div
          className="balance-header"
          onClick={() =>
            setChartCollapsed(
              (prev) => !prev
            )
          }
        >

          <div className="balance-title">

            <FaChartLine
              className="balance-title-icon"
              size={20}
            />

            <div>

              <div className="balance-title-main">
                Diễn biến điểm
              </div>

              <div className="balance-title-sub">
                {scoreChartHistory.length
                  ? `${scoreChartHistory.length} lượt biến động`
                  : "Bắt đầu từ mốc 0"}
              </div>

            </div>

          </div>

          {chartCollapsed ? (
            <FaChevronUp size={18} />
          ) : (
            <FaChevronDown size={18} />
          )}

        </div>

        {!chartCollapsed && (
          <div className="balance-body">

            <ScoreTrendChart
              players={
                chartPlayers
              }
              history={
                scoreChartHistory
              }
            />

          </div>
        )}

      </div>

      {/* Confirm OFF */}
      {showConfirmModal && (
        <div className="modal-overlay">

          <div className="modal-content">

            <h3 className="modal-title">
              Xác nhận
            </h3>

            <p>
              Đang căng mà Người này nghỉ chơi à??
            </p>

            <div className="flex justify-between space-x-4">

              <Button
                onClick={
                  confirmDeactivatePlayer
                }
                className="confirm-button bg-red-500 text-white"
              >
                Xác Nhận
              </Button>

              <Button
                onClick={
                  cancelDeactivatePlayer
                }
                className="cancel-button bg-gray-500 text-white"
              >
                Hủy
              </Button>

            </div>

          </div>

        </div>
      )}

      {/* History */}
      {history && (
        <div
          className="modal-overlay md-o-hist"
          onClick={() =>
            setHistory(null)
          }
        >

          <div
            className="modal-content md-hist"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <h3 className="modal-title">
              Lịch sử điểm
            </h3>

            <ul className="history-list">

              {[...history]
                .sort(
                  (a, b) =>
                    b.timestamp -
                    a.timestamp
                )
                .slice(0, 35)
                .map(
                  (
                    entry,
                    index
                  ) => (
                    <li
                      key={`${entry.timestamp}-${index}`}
                      className="history-entry"
                    >
                      {entry.change > 0
                        ? `+${entry.change}`
                        : entry.change}{" "}
                      điểm{" "}
                      {entry.winnerName
                        ? <b>({entry.winnerName} host) </b>
                        : ""}
                      lúc{" "}
                      {new Date(
                        entry.timestamp
                      ).toLocaleString("vi-VN")}
                    </li>
                  )
                )}

            </ul>

            <Button
              onClick={() =>
                setHistory(null)
              }
              className="close-modal-button"
            >
              Đóng
            </Button>

          </div>

        </div>
      )}

      {showMessageSetHost &&
        winner && (
          <div className="winner-message">
            🎊 Chúc mừng{" "}
            {winner.name}! 🎊
          </div>
        )}

      <style>
        {`
          .score-trend-line {
            filter: drop-shadow(
              0 0 2px rgba(255,255,255,.12)
            );
          }

          .score-trend-line-inactive {
            opacity: .42;
            stroke-dasharray: 2 2;
          }

          .score-player-label {
            filter: drop-shadow(
              0 2px 5px rgba(0,0,0,.25)
            );
          }

          @keyframes fadeOut {
            0% {
              opacity: 1;
            }

            80% {
              opacity: .5;
            }

            100% {
              opacity: 0;
            }
          }
        `}
      </style>

    </div>
  );
};

export default PlayerList;